import { useEffect, useRef, useState } from "react";
import { Checkbox, FormControlLabel, Switch } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapboxMapComponent.css";

import { getIcon } from "modules/eventIcons";
import defaultIconLayer from "../../../../../assets/images/Synchro/events-icons/roadworks.png";

function loadIconsToMap(map, icons = []) {
  return Promise.all(
    icons.map(({ id, path }) => {
      return new Promise((resolve) => {
        if (map.hasImage(id)) return resolve();
        map.loadImage(path, (err, image) => {
          if (!err && image && !map.hasImage(id)) {
            map.addImage(id, image);
          }
          resolve();
        });
      });
    })
  );
}

function getClusterPaintStyle({
  colorSteps = ["#0095b3", "#cc0036", "#E98500"],
  radiusSteps = [20, 30, 40],
  strokeColor = "#ffffff",
} = {}) {
  return {
    "circle-color": [
      "step",
      ["get", "point_count"],
      colorSteps[0],
      100,
      colorSteps[1],
      750,
      colorSteps[2],
    ],
    "circle-radius": [
      "step",
      ["get", "point_count"],
      radiusSteps[0],
      100,
      radiusSteps[1],
      750,
      radiusSteps[2],
    ],
    "circle-stroke-width": 1,
    "circle-stroke-color": strokeColor,
    "circle-emissive-strength": 1,
  };
}

function MapboxMapComponent({ layers, mapUIConfig }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const hoveredFeatureId = useRef(null);
  const clickedFeatureId = useRef({}); // { [layerId]: featureId }

  const [hasRenderedOnce, setHasRenderedOnce] = useState(false);

  // Hover popup (simple)
  const popupRef = useRef(
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
  );

  // Click popup – ίδιο UI με TrafficMonitoringMap
  const clickPopupRef = useRef(
    new mapboxgl.Popup({
      className: "my-custom-popup",
      closeButton: true,
      closeOnClick: true,
    })
  );

  mapboxgl.accessToken = mapUIConfig.map.mapToken;

  const initialMapStyle =
    mapUIConfig.defaultMapStyle === "3D"
      ? mapUIConfig.map.style.map3D
      : mapUIConfig.map.style.map2D;

  const [selectedMapStyle, setSelectedMapStyle] = useState(initialMapStyle);

  const [layerVisibility, setLayerVisibility] = useState(
    layers.reduce((acc, layer) => {
      acc[layer.id] = layer.visible ?? true;
      return acc;
    }, {})
  );

  // 🔐 Μία φορά: όταν κλείνει το click popup, καθάρισε όλα τα click feature-states
  useEffect(() => {
    const popup = clickPopupRef.current;
    if (!popup || popup.__closeHandlerBound) return;

    popup.__closeHandlerBound = true;

    popup.on("close", () => {
      const map = mapRef.current;
      if (!map) return;

      Object.entries(clickedFeatureId.current).forEach(
        ([layerId, featureId]) => {
          if (featureId == null) return;
          const sourceId = `${layerId}-source`;
          if (!map.getSource(sourceId)) return;
          map.setFeatureState(
            { source: sourceId, id: featureId },
            { click: false }
          );
        }
      );

      clickedFeatureId.current = {};
    });
  }, []);

  // ✅ Helper: prepare icons + then render layers
  const prepareAndRenderIconLayer = (map, layers, renderLayers) => {
    const iconLayers = layers.filter((l) => l.layer.includes("IconLayer"));

    iconLayers.forEach((layer) => {
      const features = layer?.source?.data?.features || [];
      const { useSourceIcon = false, setIcon } = layer.layerStyle || {};
      const usedIcons = new Set();

      features.forEach((f) => {
        if (!f.id) f.id = crypto.randomUUID();

        if (useSourceIcon) {
          if (f.properties?.icon) {
            usedIcons.add(f.properties.icon);
          } else {
            const fallbackId = `fallback-${layer.id}`;
            f.properties.icon = fallbackId;
            usedIcons.add(fallbackId);

            const iconPath = setIcon || defaultIconLayer;

            if (!map.hasImage(fallbackId)) {
              map.loadImage(iconPath, (err, image) => {
                if (!err && image && !map.hasImage(fallbackId)) {
                  map.addImage(fallbackId, image);
                }
              });
            }
          }
        } else {
          const fallbackId = setIcon
            ? `icon-set-${layer.id}`
            : `fallback-${layer.id}`;
          f.properties.icon = fallbackId;
          usedIcons.add(fallbackId);

          const iconPath = setIcon || defaultIconLayer;

          if (!map.hasImage(fallbackId)) {
            map.loadImage(iconPath, (err, image) => {
              if (!err && image && !map.hasImage(fallbackId)) {
                map.addImage(fallbackId, image);
              }
            });
          }
        }
      });

      if (layer?.source?.data) {
        layer.source.data.features = features;
      }

      const iconsToLoad = Array.from(usedIcons)
        .filter(
          (id) =>
            !map.hasImage(id) &&
            !id.startsWith("icon-set-") &&
            !id.startsWith("fallback-")
        )
        .map((id) => ({ id, path: getIcon(id) }));

      if (iconsToLoad.length) {
        loadIconsToMap(map, iconsToLoad);
      }
    });

    const waitForStyle = () => {
      if (map.isStyleLoaded()) {
        renderLayers(map);
      } else {
        requestAnimationFrame(waitForStyle);
      }
    };

    waitForStyle();
  };

  /**
   * Layer factories
   */
  const createLineLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": "round",
      "line-join": "round",
      ...(options.layout || {}),
    },
    paint: {
      "line-emissive-strength": options.lineEmissiveStrength || 1.0,
      "line-color": [
        "case",
        ["has", "colorLine"],
        ["to-color", ["get", "colorLine"]],
        options.color || "#3399ff",
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedWidth || 8,
        options.lineWidth || 3,
      ],
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.4,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.5,
        options.defaultOpacity || 1,
      ],
      ...(options.paint || {}),
    },
  });

  const createPointLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "circle",
    source: sourceId,
    layout: {
      ...(options.layout || {}),
    },
    filter: options.filter || ["all"],
    paint: {
      "circle-emissive-strength": options.lineEmissiveStrength || 1.0,
      "circle-radius": options.fixedSize
        ? options.radius || 6
        : [
            "interpolate",
            ["linear"],
            ["zoom"],
            5,
            options.radius || 6,
            15,
            options.radius || 6,
          ],
      "circle-color": [
        "case",
        ["has", "color"],
        ["to-color", ["get", "color"]],
        ["to-color", options.color || "#00aaff"],
      ],
      "circle-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.6,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.7,
        options.defaultOpacity || 1,
      ],
      "circle-stroke-color": options.strokeColor || "#ffffff",
      "circle-stroke-width": options.strokeWidth || 1,
      ...(options.paint || {}),
    },
  });

  const createFillLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "fill",
    source: sourceId,
    layout: {
      ...(options.layout || {}),
    },
    paint: {
      "fill-emissive-strength": options.lineEmissiveStrength || 1.0,
      "fill-color": [
        "case",
        ["has", "colorFill"],
        ["to-color", ["get", "colorFill"]],
        options.color || "#ffcc00",
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.4,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.6,
        options.defaultOpacity || 0.8,
      ],
      ...(options.paint || {}),
    },
  });

  const createIconLayer = (layerId, sourceId, options = {}) => {
    const defaultIcon = options.defaultIcon;
    return {
      id: layerId,
      type: "symbol",
      source: sourceId,
      layout: {
        "icon-image": defaultIcon
          ? ["coalesce", ["get", "icon"], defaultIcon]
          : ["get", "icon"],
        "icon-size": options.iconSize || 0.9,
        "icon-allow-overlap": true,
        ...(options.layout || {}),
      },
      paint: {
        "icon-opacity": [
          "case",
          ["boolean", ["feature-state", "click"], false],
          0.6,
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          1,
        ],
        ...(options.paint || {}),
      },
    };
  };

  /**
   * Cluster layers + guarded cluster interactions
   */
  const createClusterLayers = (map, layer, sourceId) => {
    const clusterLayerId = `${layer.id}-cluster`;
    const clusterCountLayerId = `${layer.id}-cluster-count`;

    const options = layer.clusterStyle || {};

    const visibility = layerVisibility[layer.id] ? "visible" : "none";

    if (!map.__interactionBound) {
      map.__interactionBound = {};
    }

    if (!map.getLayer(clusterLayerId)) {
      map.addLayer({
        id: clusterLayerId,
        type: "circle",
        source: sourceId,
        filter: ["has", "point_count"],
        slot: "middle",
        layout: { visibility },
        paint: getClusterPaintStyle({
          colorSteps: options.clusterColorSteps,
          radiusSteps: options.clusterRadiusSteps,
          strokeColor: options.clusterStrokeColor,
        }),
      });
    }

    if (!map.getLayer(clusterCountLayerId)) {
      map.addLayer({
        id: clusterCountLayerId,
        type: "symbol",
        source: sourceId,
        filter: ["has", "point_count"],
        slot: "top",
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 8, 11, 14, 14],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          visibility,
        },
        
      });
    }

    const clusterClickKey = `${layer.id}-cluster-click`;
    if (!map.__interactionBound[clusterClickKey]) {
      map.__interactionBound[clusterClickKey] = true;

      map.on("click", clusterLayerId, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [clusterLayerId],
        });

        if (!features.length) return;

        const clusterId = features[0].properties.cluster_id;

        map
          .getSource(sourceId)
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom,
            });
          });
      });
    }

    const clusterHoverKey = `${layer.id}-cluster-hover`;
    if (!map.__interactionBound[clusterHoverKey]) {
      map.__interactionBound[clusterHoverKey] = true;

      map.on("mouseenter", clusterLayerId, () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", clusterLayerId, () => {
        map.getCanvas().style.cursor = "";
      });
    }
  };

  /**
   * Render all layers + guarded hover/click
   */
  const renderLayers = (map) => {
    if (!map.__interactionBound) {
      map.__interactionBound = {};
    }

    layers.forEach((layer) => {
      const sourceId = `${layer.id}-source`;
      const visibility = layerVisibility[layer.id] ? "visible" : "none";

      // Source
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          ...layer.source,
          ...(layer.clusterStyle?.isCluster
            ? {
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: layer.clusterStyle.clusterRadius || 50,
              }
            : {}),
        });
      }

      // Cluster
      if (layer.clusterStyle?.isCluster) {
        createClusterLayers(map, layer, sourceId);
      }

      // Line
      if (layer.layer.includes("LineLayer")) {
        const lineLayer = createLineLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });

        if (!map.getLayer(layer.id)) {
          map.addLayer(lineLayer);
        }
      }

      // Point
      if (layer.layer.includes("PointLayer")) {
        const pointLayer = createPointLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
          },
          filter: layerVisibility[layer.id]
            ? ["all"]
            : ["==", "id", "___HIDE___"],
        });

        if (!map.getLayer(layer.id)) {
          map.addLayer(pointLayer);
        }
      }

      // Fill
      if (layer.layer.includes("FillLayer")) {
        const fillLayer = createFillLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });

        if (!map.getLayer(layer.id)) {
          map.addLayer(fillLayer);
        }
      }

      // Icon
      if (layer.layer.includes("IconLayer")) {
        const iconLayer = createIconLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });

        if (!map.getLayer(layer.id)) {
          map.addLayer(iconLayer);
        }
      }

      /**
       * Hover – once per layer
       */
      if (layer.canHover) {
        const hoverKey = `${layer.id}-hover`;
        if (!map.__interactionBound[hoverKey]) {
          map.__interactionBound[hoverKey] = true;

          map.on("mousemove", layer.id, (e) => {
            if (!e.features.length) return;
            const feature = e.features[0];
            const featureId = feature.id;

            if (hoveredFeatureId.current !== null) {
              map.setFeatureState(
                { source: sourceId, id: hoveredFeatureId.current },
                { hover: false }
              );
            }

            hoveredFeatureId.current = featureId;

            map.setFeatureState(
              { source: sourceId, id: featureId },
              { hover: true }
            );
            map.getCanvas().style.cursor = "pointer";

            const msg = feature.properties?.displayMessage;
            if (msg && msg.trim()) {
              popupRef.current
                .setLngLat(e.lngLat)
                .setHTML(`<div style='font-size: 14px;'>${msg}</div>`)
                .addTo(map);
            }
          });

          map.on("mouseleave", layer.id, () => {
            if (hoveredFeatureId.current !== null) {
              map.setFeatureState(
                { source: sourceId, id: hoveredFeatureId.current },
                { hover: false }
              );
            }
            hoveredFeatureId.current = null;
            map.getCanvas().style.cursor = "";
            popupRef.current.remove();
          });
        }
      }

      /**
       * Click – once per layer, shared popup με UI σαν TrafficMonitoringMap
       */
      if (layer.canClick) {
        const clickKey = `${layer.id}-click`;
        if (!map.__interactionBound[clickKey]) {
          map.__interactionBound[clickKey] = true;

          map.on("click", layer.id, (e) => {
            if (!e.features.length) return;

            const feature = e.features[0];
            const featureId = feature.id;

            const prevId = clickedFeatureId.current[layer.id];
            if (prevId !== undefined) {
              map.setFeatureState(
                { source: sourceId, id: prevId },
                { click: false }
              );
            }

            clickedFeatureId.current[layer.id] = featureId;

            map.setFeatureState(
              { source: sourceId, id: featureId },
              { click: true }
            );

            const msg = feature.properties?.displayMessage;
            if (msg && msg.trim()) {
              const html = `
                <div class="custom-popup-content">
                  ${msg}
                </div>
              `;

              clickPopupRef.current
                .setLngLat(e.lngLat)
                .setHTML(html)
                .addTo(map);
            }
          });
        }
      }
    });
  };

  /**
   * Init map – once
   */
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: selectedMapStyle,
      center: mapUIConfig.map.center,
      zoom: mapUIConfig.map.zoom,
      pitch: mapUIConfig.map.pitch,
      bearing: mapUIConfig.map.bearing,
      minZoom: mapUIConfig.map.minZoom,
      maxZoom: mapUIConfig.map.maxZoom,
    });

    mapRef.current = map;

    map.once("style.load", () => {
      prepareAndRenderIconLayer(map, layers, renderLayers);
    });

    map.on("styleimagemissing", (e) => {
      const id = e.id;
      const match = id.match(/^icon-(\d+)$/);
      if (!match) return;

      const path = getIcon(id);
      map.loadImage(path, (err, image) => {
        if (!err && image) {
          map.addImage(id, image);
        }
      });
    });

    return () => {
      map.remove();
    };
  }, []);

  /**
   * Change style (2D / 3D)
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setStyle(selectedMapStyle);
    map.once("style.load", () => {
      prepareAndRenderIconLayer(map, layers, renderLayers);
    });
  }, [selectedMapStyle]);

  /**
   * Data changes
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const allSourcesReady = layers.every(
      (l) => l.source?.data && l.source?.data?.type === "FeatureCollection"
    );

    if (allSourcesReady) {
      prepareAndRenderIconLayer(map, layers, renderLayers);
      if (!hasRenderedOnce) setHasRenderedOnce(true);
    }
  }, [layers, hasRenderedOnce]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || hasRenderedOnce) return;

    prepareAndRenderIconLayer(map, layers, renderLayers);
  }, [hasRenderedOnce, layers]);

  /**
   * Toggle 3D
   */
  const handleToggle3D = () => {
    setSelectedMapStyle((prev) =>
      prev === mapUIConfig.map.style.map3D
        ? mapUIConfig.map.style.map2D
        : mapUIConfig.map.style.map3D
    );
  };

  /**
   * Show / hide layer
   */
  const handleLayerToggle = (layerId) => {
    setLayerVisibility((prev) => {
      const newState = { ...prev, [layerId]: !prev[layerId] };
      const map = mapRef.current;

      if (map) {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(
            layerId,
            "visibility",
            newState[layerId] ? "visible" : "none"
          );
        }

        const clusterLayerIds = [
          `${layerId}-cluster`,
          `${layerId}-cluster-count`,
        ];

        clusterLayerIds.forEach((clusterId) => {
          if (map.getLayer(clusterId)) {
            map.setLayoutProperty(
              clusterId,
              "visibility",
              newState[layerId] ? "visible" : "none"
            );
          }
        });
      }

      return newState;
    });
  };

  return (
    <div className="map-container">
      {mapUIConfig.displayControlPanel && (
        <div className="control-panel">
          {mapUIConfig.displayLayerList && (
            <div className="layer-list-toggle-controls">
              {layers.map((layer) => (
                <FormControlLabel
                  key={layer.id}
                  control={
                    <Checkbox
                      checked={layerVisibility[layer.id]}
                      onChange={() => handleLayerToggle(layer.id)}
                    />
                  }
                  label={layer.name}
                />
              ))}
            </div>
          )}

          {mapUIConfig.display3DToggle && (
            <FormControlLabel
              control={
                <Switch
                  checked={selectedMapStyle === mapUIConfig.map.style.map3D}
                  onChange={handleToggle3D}
                />
              }
              label={
                selectedMapStyle === mapUIConfig.map.style.map3D ? "3D" : "2D"
              }
            />
          )}
        </div>
      )}

      <div
        id={mapUIConfig.id}
        ref={mapContainerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export default MapboxMapComponent;
